import { useEffect, useState } from "react";
import { Hourglass, Flag, Bank } from "@phosphor-icons/react";
import { fmtDate, ageLabel } from "@/lib/fers";
import { usd } from "@/lib/format";
import { incomeLines, toDate } from "@/lib/snapshot";

const DAY = 864e5;

function LiveCountdown({ date }) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);
  const ms = Math.max(0, toDate(date) - now);
  if (!ms) return <p className="font-display text-xl font-semibold text-grad-money" data-testid="snapshot-countdown-done">Retirement day has arrived</p>;
  const parts = [[Math.floor(ms / DAY).toLocaleString(), "days"], [Math.floor((ms % DAY) / 36e5), "hrs"], [Math.floor((ms % 36e5) / 6e4), "min"], [Math.floor((ms % 6e4) / 1e3), "sec"]];
  return (
    <div className="flex gap-3" data-testid="snapshot-countdown">
      {parts.map(([v, l]) => <div key={l} className="text-center min-w-[52px]"><p className="font-mono-num text-2xl sm:text-3xl font-semibold tabular-nums" data-testid={`snapshot-countdown-${l}`}>{String(v).padStart(2, "0")}</p><p className="text-[10px] uppercase tracking-widest text-muted-foreground">{l}</p></div>)}
    </div>
  );
}

function IncomeTable({ d }) {
  return (
    <div className="glass p-5" data-testid="snapshot-income">
      <p className="text-xs uppercase tracking-wider text-muted-foreground">Projected monthly income (before tax)</p>
      <ul className="mt-3 divide-y divide-border/60">
        {incomeLines(d).map((l) => (
          <li key={l.key} className="flex justify-between gap-3 py-2.5 text-sm" data-testid={`snapshot-line-${l.key}`}>
            <div><p className="font-medium">{l.label}</p><p className="text-xs text-muted-foreground">{l.note}</p></div>
            <span className="font-mono-num font-medium" data-testid={`snapshot-line-${l.key}-value`}>{usd(l.value)}</span>
          </li>
        ))}
      </ul>
      <div className="grid grid-cols-2 gap-3 mt-3 pt-3 border-t border-border">
        <div><p className="text-xs text-muted-foreground">First-year total</p><p className="font-mono-num text-lg font-semibold" data-testid="snapshot-first-year">{usd(d.first_year_monthly)}/mo</p></div>
        <div><p className="text-xs text-muted-foreground">Once all income starts (age {ageLabel(d.full_age)})</p><p className="font-mono-num text-lg font-semibold text-grad-money" data-testid="snapshot-full-monthly">{usd(d.full_monthly)}/mo</p></div>
      </div>
    </div>
  );
}

export function SnapshotView({ d }) {
  return (
    <div className="grid gap-4 md:grid-cols-2" data-testid="snapshot-view">
      <div className="space-y-4">
        <div className="glass p-5">
          <p className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1.5"><Hourglass size={14} className="text-emerald-600" />Countdown to retirement</p>
          <p className="font-display text-xl font-semibold mt-1" data-testid="snapshot-retirement-date">{fmtDate(toDate(d.retirement_date))}</p>
          <p className="text-xs text-muted-foreground mb-3">{d.system} · retiring at {ageLabel(d.retire_age)}{d.exact_date ? "" : " · approximate date"}</p>
          <LiveCountdown date={d.retirement_date} />
        </div>
        <div className="glass p-5 flex items-center justify-between gap-3">
          <div><p className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1.5"><Bank size={14} className="text-cyan-600" />Current TSP balance</p>
            <p className="font-mono-num text-2xl font-semibold mt-1" data-testid="snapshot-tsp-balance">{usd(d.tsp_balance)}</p></div>
        </div>
        {d.next_milestone && (
          <div className="glass p-5" data-testid="snapshot-next-milestone">
            <p className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1.5"><Flag size={14} className="text-cyan-600" />Next milestone</p>
            <p className="text-sm font-medium mt-1">{d.next_milestone.label}</p><p className="text-xs text-muted-foreground">{fmtDate(toDate(d.next_milestone.date))}</p>
          </div>
        )}
      </div>
      <IncomeTable d={d} />
    </div>
  );
}
